import React from "react";
import PropTypes from "prop-types";
import { statusTextToNumber, priorityTextToNumber } from "./taskUtils";

/**
 * Reusable Statistic Card Component
 */
const StatCard = ({
  icon,
  iconBg = "",
  iconColor = "",
  value,
  label,
  trend,
  isTrendNegative = false,
  isActive = false,
  onClick,
}) => {
  return (
    <button
      onClick={onClick}
      type="button"
      className={`relative overflow-hidden cursor-pointer rounded-xl border bg-white px-4 py-3 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md group
        ${isActive ? "border-blue-500 ring-2 ring-blue-200 shadow-md -translate-y-0.5" : "border-gray-200"}`}
    >
      {/* Top Accent Line — always visible when active */}
      <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-600 to-blue-400 transition-opacity
        ${isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} />

      {/* Active indicator dot */}
      {isActive && (
        <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-blue-500" />
      )}

      {/* Header */}
      <div className="mb-2 flex items-center justify-between">
        <div className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm ${iconBg} ${iconColor}
          ${isActive ? "scale-110" : ""} transition-transform`}>
          <i className={icon} />
        </div>
        {trend && (
          <span className={`text-xs font-bold ${isTrendNegative ? "text-red-500" : "text-green-600"}`}>
            {trend}
          </span>
        )}
      </div>

      {/* Value */}
      <div className={`mb-1 text-2xl font-extrabold leading-none tracking-tight
        ${isActive ? "text-blue-600" : "text-gray-900"}`}>
        {value}
      </div>

      {/* Label */}
      <div className={`text-xs font-semibold ${isActive ? "text-blue-500" : "text-gray-500"}`}>
        {label}
        {isActive && <span className="ml-1 text-blue-400"></span>}
      </div>
    </button>
  );
};

StatCard.propTypes = {
  icon: PropTypes.string.isRequired,
  iconBg: PropTypes.string,
  iconColor: PropTypes.string,
  value: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  trend: PropTypes.string,
  isTrendNegative: PropTypes.bool,
  isActive: PropTypes.bool,
  onClick: PropTypes.func,
};

/**
 * Dashboard Statistics Component
 */
export const DashboardStats = ({ tasks = [], onFilterClick, activeFilter }) => {
  const now = new Date();

  const totalTasks = tasks.length;

  const inProgressTasks = tasks.filter(
    (task) => statusTextToNumber(task.status) === 2
  ).length;

  const completedTasks = tasks.filter(
    (task) => statusTextToNumber(task.status) === 3
  ).length;

  const highPriorityTasks = tasks.filter(
    (task) => priorityTextToNumber(task.priority) === 1
  ).length;

  const dueSoonTasks = tasks.filter((task) => {
    if (!task.dueDate) return false;

    const dueDate = new Date(task.dueDate);
    const diffInDays = (dueDate - now) / (1000 * 60 * 60 * 24);

    return diffInDays >= 0 && diffInDays <= 7;
  }).length;

 return (
    <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
      <StatCard
        icon="fas fa-tasks"
        iconColor="text-blue-600"
        value={totalTasks}
        label="Total Tasks"
        isActive={activeFilter === "all"}
        onClick={() => onFilterClick?.("all")}
      />
      <StatCard
        icon="fas fa-spinner"
        iconColor="text-yellow-600"
        value={inProgressTasks}
        label="In Progress"
        isActive={activeFilter === "inprogress"}
        onClick={() => onFilterClick?.("inprogress")}
      />
      <StatCard
        icon="fas fa-circle-check"
        iconColor="text-sky-600"
        value={completedTasks}
        label="Completed"
        isActive={activeFilter === "closed"}
        onClick={() => onFilterClick?.("closed")}
      />
      <StatCard
        icon="fas fa-fire"
        iconColor="text-red-600"
        value={highPriorityTasks}
        label="High Priority"
        isActive={activeFilter === "highpriority"}
        onClick={() => onFilterClick?.("highpriority")}
      />
      <StatCard
        icon="fas fa-hourglass-end"
        iconColor="text-green-600"
        value={dueSoonTasks}
        label="Due Soon"
        isActive={activeFilter === "duesoon"}
        onClick={() => onFilterClick?.("duesoon")}
      />
    </div>
  );
};

DashboardStats.propTypes = {
  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      status: PropTypes.string,
      priority: PropTypes.string,
      dueDate: PropTypes.string,
    })
  ),
  onFilterClick: PropTypes.func,
  activeFilter: PropTypes.string,
};